import type { TeamMember } from '@nexo/types';
import { Avatar, Button, cn, useContextMenu, useToast } from '@nexo/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Copy, Crown, MessageSquare, Shield, UserMinus, UserPlus, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useTeam } from '../../layouts/TeamContext';
import { api } from '../../lib/api';
import { cache } from '../../lib/cache';
import { useMe } from '../auth/AuthProvider';
import { RoleChip, roleColors } from './roles';
import { UserBadges, statusLabel } from '../status/UserBadges';
import { can } from '../../lib/permissions';
import { errorMessage } from '../../lib/forms';
import { openUserCard, useOpenUserCardId } from './UserCard';

/** Team members split by presence (online first). */
export function MembersPanel() {
  const { team, members, online, openDialog, roleById, roles } = useTeam();
  const me = useMe();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const openDm = useMutation({
    mutationFn: (userId: string) => api.channels.openDm(team.id, userId),
    onSuccess: (channel) => {
      cache.upsertChannel(queryClient, channel);
      void navigate(`/app/t/${team.id}/c/${channel.id}`);
    },
  });

  const toast = useToast();
  const menu = useContextMenu();
  // Clicking a person opens their card beside the row (like Discord).
  const cardUserId = useOpenUserCardId();
  const kick = useMutation({
    mutationFn: (userId: string) => api.teams.removeMember(team.id, userId),
    onError: (err) =>
      toast({ title: t('No se pudo expulsar'), description: errorMessage(err), tone: 'error' }),
  });
  const myRole = members.find((m) => m.id === me.id)?.role;
  const canKick = (member: TeamMember) =>
    member.id !== me.id &&
    member.role !== 'owner' &&
    can(team, 'kick_members') &&
    (member.role !== 'admin' || myRole === 'owner');

  // Right click on a person (like Discord).
  const entriesFor = (member: TeamMember) => [
    member.id !== me.id && {
      label: t('Enviar mensaje'),
      icon: <MessageSquare />,
      onSelect: () => openDm.mutate(member.id),
    },
    {
      label: t('Ver en Miembros'),
      icon: <Users />,
      onSelect: () => openDialog('members'),
    },
    {
      label: t('Copiar nombre'),
      icon: <Copy />,
      onSelect: () => void navigator.clipboard.writeText(member.displayName),
    },
    canKick(member) && ('separator' as const),
    canKick(member) && {
      label: t('Expulsar del equipo'),
      icon: <UserMinus />,
      danger: true,
      onSelect: () => {
        if (window.confirm(t('¿Expulsar a {{name}} del equipo?', { name: member.displayName }))) {
          kick.mutate(member.id);
        }
      },
    },
  ];

  // Like Discord: online people grouped by their highest role (owner, admins, then each custom
  // role in order, then everyone else); offline people at the end.
  const isOnline = (m: TeamMember) => !online || online.has(m.id);
  const firstRole = (m: TeamMember) => roles.find((r) => m.customRoleIds.includes(r.id));
  const roleTextColor = (m: TeamMember) => {
    const role = firstRole(m);
    return role ? roleColors[role.color].text : undefined;
  };
  const live = members.filter(isOnline);
  const placed = new Set<string>();
  const take = (people: TeamMember[]) => {
    const fresh = people.filter((m) => !placed.has(m.id));
    fresh.forEach((m) => placed.add(m.id));
    return fresh;
  };
  const groups: {
    key: string;
    title: string;
    people: TeamMember[];
    dim?: boolean;
    dot?: string;
  }[] = [
    { key: 'owner', title: t('Propietario'), people: take(live.filter((m) => m.role === 'owner')) },
    {
      key: 'admins',
      title: t('Administradores'),
      people: take(live.filter((m) => m.role === 'admin')),
    },
    ...roles.map((role) => ({
      key: role.id,
      title: role.name,
      dot: roleColors[role.color].dot,
      people: take(live.filter((m) => firstRole(m)?.id === role.id)),
    })),
    { key: 'members', title: online ? t('En línea') : t('Miembros'), people: take(live) },
    {
      key: 'offline',
      title: t('Desconectados'),
      people: members.filter((m) => !isOnline(m)),
      dim: true,
    },
  ];

  return (
    <div className="scrollbar-thin flex-1 overflow-y-auto px-2 py-3">
      {groups.map(
        (group) =>
          group.people.length > 0 && (
            <section key={group.key} className="mb-4">
              <h3 className="mb-1 flex items-center gap-1.5 px-2 text-[11px] font-semibold tracking-wider text-subtle uppercase">
                {group.dot && <span className={cn('size-2 rounded-full', group.dot)} />}
                <span className="truncate">{group.title}</span> · {group.people.length}
              </h3>
              {group.people.map((member) => (
                <button
                  key={member.id}
                  type="button"
                  data-user-card={member.id}
                  onClick={(event) => openUserCard(member.id, event)}
                  onContextMenu={menu.open(() => entriesFor(member))}
                  aria-haspopup="dialog"
                  aria-expanded={cardUserId === member.id}
                  className={cn(
                    'flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 text-left hover:bg-surface-2',
                    cardUserId === member.id && 'bg-surface-2',
                    group.dim && 'opacity-60',
                  )}
                >
                  <Avatar
                    id={member.id}
                    name={member.displayName}
                    src={member.avatarUrl}
                    size="sm"
                    online={online ? online.has(member.id) : undefined}
                  />
                  <span className="min-w-0 flex-1">
                    {/* Only the name is shortened: «(tú)» and the badges stay visible. */}
                    <span className="flex min-w-0 items-center gap-1.5 text-sm font-medium">
                      <span
                        className={cn('min-w-0 truncate', roleTextColor(member))}
                        title={member.displayName}
                      >
                        {member.displayName}
                      </span>
                      {member.id === me.id && (
                        <span className="shrink-0 font-normal text-subtle">{t('(tú)')}</span>
                      )}
                      <UserBadges user={member} />
                    </span>
                    {(member.status?.text || member.title) && (
                      <span className="block truncate text-xs text-muted">
                        {member.status?.text ? statusLabel(member) : member.title}
                      </span>
                    )}
                    {member.customRoleIds.length > 0 && (
                      <span className="mt-0.5 flex flex-wrap gap-1">
                        {member.customRoleIds.map((id) => {
                          const role = roleById(id);
                          return role && <RoleChip key={id} role={role} />;
                        })}
                      </span>
                    )}
                  </span>
                  {member.role === 'owner' && (
                    <Crown className="size-4 shrink-0 text-amber-500" aria-label={t('Propietario')}>
                      <title>{t('Propietario')}</title>
                    </Crown>
                  )}
                  {member.role === 'admin' && (
                    <Shield className="size-4 shrink-0 text-accent" aria-label={t('Admin')}>
                      <title>{t('Admin')}</title>
                    </Shield>
                  )}
                </button>
              ))}
            </section>
          ),
      )}
      {can(team, 'invite_members') && (
        <Button
          variant="secondary"
          size="sm"
          icon={<UserPlus className="size-4" />}
          className="mx-2 w-[calc(100%-1rem)]"
          onClick={() => openDialog('members')}
        >
          {t('Invitar al equipo')}
        </Button>
      )}
      {menu.element}
    </div>
  );
}
